import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, qs } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ago, dateTime, eventLabel, kph } from '../lib/format'
import type { AlertRule, FleetEvent, Fleet, ListResponse } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { DataTable, Toolbar, type Column } from '../components/DataTable'
import { Button, Chip, Field, Modal, Panel, SeverityChip, Toggle, toast } from '../components/ui'

const RANGES = [
  { key: '1d', label: '24 h' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
]

export function Events() {
  const { can } = useAuth()
  const [tab, setTab] = useState<'feed' | 'rules'>('feed')

  return (
    <Page
      title="Alerts"
      subtitle="Events raised by the rule engine, and the rules that raise them"
      actions={
        <div className="flex gap-1">
          {(['feed', 'rules'] as const).map((t) => {
            if (t === 'rules' && !can('alert.view')) return null
            return (
              <button
                key={t}
                onClick={() => setTab(t)}
                className="h-8 px-3 rounded-[3px] border text-[12px] font-[var(--font-label)] font-semibold uppercase tracking-wider transition-colors"
                style={{
                  borderColor: tab === t ? 'var(--accent)' : 'var(--color-rule)',
                  background: tab === t ? 'var(--accent-dim)' : 'transparent',
                  color: tab === t ? 'var(--accent-text)' : 'var(--color-ink-3)',
                }}
              >
                {t === 'feed' ? 'Event feed' : 'Rules'}
              </button>
            )
          })}
        </div>
      }
    >
      {tab === 'feed' ? <EventFeed /> : <Rules />}
    </Page>
  )
}

/* ===========================================================================
   Feed
   =========================================================================== */

function EventFeed() {
  const { can } = useAuth()
  const qc = useQueryClient()

  const [range, setRange] = useState('1d')
  const [severity, setSeverity] = useState('')
  const [unackOnly, setUnackOnly] = useState(true)
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const limit = 100

  const list = useQuery({
    queryKey: ['events', range, severity, unackOnly, offset],
    queryFn: () =>
      api<ListResponse<FleetEvent>>(
        `/events${qs({ range, severity, unacknowledged: unackOnly, limit, offset })}`,
      ),
    refetchInterval: 30_000,
  })

  const ack = useMutation({
    mutationFn: (ids: string[]) => api('/events/ack', { body: { ids } }),
    onSuccess: (_d, ids) => {
      toast(`${ids.length} alert${ids.length > 1 ? 's' : ''} acknowledged`, 'ok')
      setSelected(new Set())
      void qc.invalidateQueries({ queryKey: ['events'] })
      void qc.invalidateQueries({ queryKey: ['summary'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const rows = list.data?.items ?? []
  const allSelected = rows.length > 0 && rows.every((e) => selected.has(e.id))

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const columns: Column<FleetEvent>[] = [
    ...(can('alert.ack')
      ? [
          {
            key: 'select',
            width: '34px',
            header: (
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() =>
                  setSelected(allSelected ? new Set() : new Set(rows.map((e) => e.id)))
                }
                aria-label="Select all"
              />
            ),
            render: (e: FleetEvent) => (
              <input
                type="checkbox"
                checked={selected.has(e.id)}
                onChange={() => toggle(e.id)}
                onClick={(ev) => ev.stopPropagation()}
                disabled={Boolean(e.acknowledgedAt)}
                aria-label={`Select ${e.type}`}
              />
            ),
          },
        ]
      : []),
    {
      key: 'severity',
      header: 'Severity',
      width: '96px',
      render: (e) => <SeverityChip severity={e.severity} />,
    },
    {
      key: 'type',
      header: 'Event',
      width: '160px',
      render: (e) => <span className="text-[var(--color-ink)]">{eventLabel(e.type)}</span>,
    },
    {
      key: 'message',
      header: 'Detail',
      render: (e) => (
        <div className="min-w-0">
          <div className="truncate">{e.message ?? '—'}</div>
          {e.geofenceName && (
            <div className="text-[11px] text-[var(--color-ink-4)]">Geofence: {e.geofenceName}</div>
          )}
          {Boolean(e.payload?.alarmId) && e.vehicleId && (
            <Link
              className="text-[11px] hover:underline"
              style={{ color: 'var(--accent-text)' }}
              to={`/vehicles/${e.vehicleId}?alarm=${String(e.payload!.alarmId)}`}
              onClick={(ev) => ev.stopPropagation()}
            >
              Photos and clip
            </Link>
          )}
        </div>
      ),
    },
    {
      key: 'vehicle',
      header: 'Vehicle',
      width: '150px',
      render: (e) =>
        e.vehicleId ? (
          <Link
            className="hover:text-[var(--accent)] transition-colors"
            to={`/vehicles/${e.vehicleId}`}
            onClick={(ev) => ev.stopPropagation()}
          >
            {e.vehicleName ?? 'Vehicle'}
          </Link>
        ) : (
          '—'
        ),
    },
    {
      key: 'speed',
      header: 'Speed',
      numeric: true,
      width: '80px',
      render: (e) => (e.speedKph != null ? kph(e.speedKph) : '—'),
    },
    {
      key: 'at',
      header: 'When',
      numeric: true,
      width: '140px',
      render: (e) => <span title={dateTime(e.at)}>{ago(e.at)}</span>,
    },
    {
      key: 'ack',
      header: '',
      width: '40px',
      render: (e) =>
        e.acknowledgedAt ? (
          <span title={`Acknowledged ${dateTime(e.acknowledgedAt)}`} style={{ color: 'var(--text-ok)' }}>
            ✓
          </span>
        ) : null,
    },
  ]

  return (
    <>
      <Toolbar
        actions={
          selected.size > 0 && (
            <Button variant="primary" onClick={() => ack.mutate([...selected])} disabled={ack.isPending}>
              <Icon name="shield" size={13} />
              Acknowledge {selected.size}
            </Button>
          )
        }
      >
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => {
                setRange(r.key)
                setOffset(0)
              }}
              className="h-[34px] px-3 rounded-[3px] border text-[12px] transition-colors"
              style={{
                borderColor: range === r.key ? 'var(--accent)' : 'var(--color-rule)',
                background: range === r.key ? 'var(--accent-dim)' : 'transparent',
                color: range === r.key ? 'var(--accent-text)' : 'var(--color-ink-3)',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        <select className="field w-auto" value={severity} onChange={(e) => setSeverity(e.target.value)}>
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>

        <Toggle checked={unackOnly} onChange={setUnackOnly} label="Unacknowledged only" />
      </Toolbar>

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(e) => e.id}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          total={list.data?.total}
          limit={limit}
          offset={offset}
          onPage={setOffset}
          empty={{
            title: unackOnly ? 'Nothing outstanding' : 'No events in this period',
            body: unackOnly
              ? 'Every alert in this window has been acknowledged.'
              : 'Alerts appear here as the rule engine raises them.',
          }}
        />
      </Panel>
    </>
  )
}

/* ===========================================================================
   Rules
   =========================================================================== */

function Rules() {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [editing, setEditing] = useState<AlertRule | null>(null)
  const [creating, setCreating] = useState(false)

  const list = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () => api<{ items: AlertRule[]; eventTypes: string[] }>('/alert-rules'),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api(`/alert-rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast('Rule deleted', 'ok')
      void qc.invalidateQueries({ queryKey: ['alert-rules'] })
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const columns: Column<AlertRule>[] = [
    {
      key: 'name',
      header: 'Rule',
      render: (r) => (
        <div>
          <div className="text-[var(--color-ink)]">{r.name}</div>
          <div className="text-[11px] text-[var(--color-ink-4)]">{eventLabel(r.eventType)}</div>
        </div>
      ),
    },
    { key: 'severity', header: 'Severity', width: '100px', render: (r) => <SeverityChip severity={r.severity} /> },
    {
      key: 'scope',
      header: 'Applies to',
      render: (r) =>
        r.vehicleIds.length === 0 && r.fleetIds.length === 0 ? (
          <span className="text-[var(--color-ink-4)]">Every vehicle</span>
        ) : (
          <span>
            {r.vehicleIds.length > 0 && `${r.vehicleIds.length} vehicle(s)`}
            {r.vehicleIds.length > 0 && r.fleetIds.length > 0 && ', '}
            {r.fleetIds.length > 0 && `${r.fleetIds.length} fleet(s)`}
          </span>
        ),
    },
    {
      key: 'channels',
      header: 'Notify via',
      render: (r) => (
        <div className="flex gap-1 flex-wrap">
          {r.channels.map((c) => (
            <Chip key={c} tone={c === 'web' ? 'muted' : 'accent'}>
              {c}
            </Chip>
          ))}
        </div>
      ),
    },
    {
      key: 'cooldown',
      header: 'Cooldown',
      numeric: true,
      width: '90px',
      render: (r) => `${r.cooldownS}s`,
    },
    {
      key: 'active',
      header: 'State',
      width: '80px',
      render: (r) => (r.active ? <Chip tone="ok">On</Chip> : <Chip tone="muted">Off</Chip>),
    },
    {
      key: 'actions',
      header: '',
      width: '70px',
      render: (r) =>
        can('alert.edit') && (
          <div className="flex justify-end gap-1">
            <button className="btn btn-ghost btn-sm px-1.5" onClick={() => setEditing(r)} title="Edit">
              <Icon name="edit" size={13} />
            </button>
            <button
              className="btn btn-ghost btn-sm px-1.5"
              style={{ color: 'var(--text-critical)' }}
              title="Delete"
              onClick={() => confirm(`Delete the rule “${r.name}”?`) && remove.mutate(r.id)}
            >
              <Icon name="trash" size={13} />
            </button>
          </div>
        ),
    },
  ]

  return (
    <>
      <Toolbar
        actions={
          can('alert.edit') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Icon name="plus" size={13} />
              New rule
            </Button>
          )
        }
      />

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={list.data?.items}
          rowKey={(r) => r.id}
          loading={list.isLoading}
          error={list.error}
          empty={{
            title: 'No alert rules',
            body: 'Rules decide who gets told when something happens. Without one, events are still recorded but nobody is notified.',
            action: can('alert.edit') ? (
              <Button variant="primary" onClick={() => setCreating(true)}>
                Create the first rule
              </Button>
            ) : undefined,
          }}
        />
      </Panel>

      {(creating || editing) && (
        <RuleForm
          rule={editing}
          eventTypes={list.data?.eventTypes ?? []}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
          onSaved={() => {
            setCreating(false)
            setEditing(null)
            void qc.invalidateQueries({ queryKey: ['alert-rules'] })
          }}
        />
      )}
    </>
  )
}

function RuleForm({
  rule,
  eventTypes,
  onClose,
  onSaved,
}: {
  rule: AlertRule | null
  eventTypes: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const [form, setForm] = useState({
    name: rule?.name ?? '',
    eventType: rule?.eventType ?? 'overspeed',
    severity: rule?.severity ?? 'warning',
    speedKph: String((rule?.config as { speed_kph?: number })?.speed_kph ?? ''),
    cooldownS: String(rule?.cooldownS ?? 300),
    recipients: (rule?.recipients ?? []).join(', '),
    webhookUrl: rule?.webhookUrl ?? '',
    active: rule?.active ?? true,
    channels: new Set(rule?.channels ?? ['web']),
    fleetIds: new Set(rule?.fleetIds ?? []),
  })

  const fleets = useQuery({ queryKey: ['fleets'], queryFn: () => api<{ items: Fleet[] }>('/fleets') })

  const needsSpeed = form.eventType === 'overspeed'
  const needsRecipients = form.channels.has('email') || form.channels.has('sms')

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        eventType: form.eventType,
        severity: form.severity,
        config: needsSpeed && form.speedKph ? { speed_kph: Number(form.speedKph) } : {},
        fleetIds: [...form.fleetIds],
        vehicleIds: [],
        geofenceIds: [],
        channels: [...form.channels],
        recipients: form.recipients
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        webhookUrl: form.webhookUrl.trim(),
        cooldownS: Number(form.cooldownS) || 300,
        active: form.active,
      }
      return rule
        ? api(`/alert-rules/${rule.id}`, { method: 'PATCH', body })
        : api('/alert-rules', { body })
    },
    onSuccess: () => {
      toast(rule ? 'Rule updated' : 'Rule created', 'ok')
      onSaved()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  function toggleChannel(c: string) {
    setForm((f) => {
      const next = new Set(f.channels)
      next.has(c) ? next.delete(c) : next.add(c)
      return { ...f, channels: next }
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={rule ? `Edit ${rule.name}` : 'New alert rule'}
      width={560}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!form.name.trim() || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save rule'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <Field label="Name" required>
          <input
            className="field"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Speeding over 110 on the motorway fleet"
            autoFocus
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-3.5">
          <Field label="When this happens" required>
            <select
              className="field"
              value={form.eventType}
              onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value }))}
            >
              {eventTypes.map((t) => (
                <option key={t} value={t}>
                  {eventLabel(t)}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Severity">
            <select
              className="field"
              value={form.severity}
              onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value as AlertRule['severity'] }))}
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="critical">Critical</option>
            </select>
          </Field>
        </div>

        {needsSpeed && (
          <Field label="Only above" hint="km/h — leave blank to use each vehicle's own limit">
            <input
              className="field tnum"
              type="number"
              value={form.speedKph}
              onChange={(e) => setForm((f) => ({ ...f, speedKph: e.target.value }))}
              placeholder="110"
            />
          </Field>
        )}

        <Field label="Limit to fleets" hint="None selected means every vehicle in the account">
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {fleets.data?.items.map((fl) => {
              const on = form.fleetIds.has(fl.id)
              return (
                <button
                  key={fl.id}
                  type="button"
                  onClick={() =>
                    setForm((f) => {
                      const next = new Set(f.fleetIds)
                      next.has(fl.id) ? next.delete(fl.id) : next.add(fl.id)
                      return { ...f, fleetIds: next }
                    })
                  }
                  className="chip"
                  style={{
                    color: on ? 'var(--accent-text)' : 'var(--color-ink-3)',
                    borderColor: on ? 'var(--accent)' : 'var(--color-rule)',
                    background: on ? 'var(--accent-dim)' : 'transparent',
                  }}
                >
                  {fl.name}
                </button>
              )
            })}
            {!fleets.data?.items.length && (
              <span className="text-[12px] text-[var(--color-ink-4)]">No fleets defined</span>
            )}
          </div>
        </Field>

        <Field label="Notify via">
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            {['web', 'email', 'sms', 'push', 'webhook'].map((c) => {
              const on = form.channels.has(c)
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleChannel(c)}
                  className="chip"
                  style={{
                    color: on ? 'var(--accent-text)' : 'var(--color-ink-3)',
                    borderColor: on ? 'var(--accent)' : 'var(--color-rule)',
                    background: on ? 'var(--accent-dim)' : 'transparent',
                  }}
                >
                  {c}
                </button>
              )
            })}
          </div>
        </Field>

        {needsRecipients && (
          <Field label="Recipients" hint="Comma-separated email addresses or phone numbers">
            <input
              className="field"
              value={form.recipients}
              onChange={(e) => setForm((f) => ({ ...f, recipients: e.target.value }))}
              placeholder="dispatch@company.com, +31612345678"
            />
          </Field>
        )}

        {form.channels.has('webhook') && (
          <Field label="Webhook URL">
            <input
              className="field"
              value={form.webhookUrl}
              onChange={(e) => setForm((f) => ({ ...f, webhookUrl: e.target.value }))}
              placeholder="https://hooks.example.com/fleet"
            />
          </Field>
        )}

        <div className="grid sm:grid-cols-2 gap-3.5 items-end">
          <Field
            label="Cooldown"
            hint="Seconds before the same vehicle can trigger this rule again"
          >
            <input
              className="field tnum"
              type="number"
              value={form.cooldownS}
              onChange={(e) => setForm((f) => ({ ...f, cooldownS: e.target.value }))}
            />
          </Field>
          <div className="pb-2">
            <Toggle
              checked={form.active}
              onChange={(v) => setForm((f) => ({ ...f, active: v }))}
              label="Rule is active"
            />
          </div>
        </div>
      </div>
    </Modal>
  )
}
